import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    Colors,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    PieController,
    Tooltip,
    Filler,
    TimeScale,
} from 'chart.js'

import ChartDataLabels from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(
    LineController,
    LineElement,
    PointElement,
    PieController,
    ArcElement,
    BarController,
    BarElement,
    LinearScale,
    CategoryScale,
    Colors,
    Tooltip,
    Legend,
    Filler,
    TimeScale,
)
import 'chartjs-adapter-date-fns';

function newSTChart(container, options={}, type="bar") {
    return new STChart(container, options, type);
}

class STChart {
    constructor(container, options={}, type="bar", stacked=false) {
        this.container = container;
        this.options = options;
        this.type = type;
        this.stacked = stacked;
        this.plugins = []
        this.chartColorMap = new Map()
        this.chartColorMap.set('light', {'text': '#2e333d', 'scaleLines': '#2e333d'});
        this.chartColorMap.set('dark', {'text': '#ebecf0', 'scaleLines': '#999999'});
        this.chart = null;
        if(Object.keys(this.options).length === 0){
            this.options = this.generateDefaultOptions();
        }
    }

    render(x, y, labels="") {
        if(!Array.isArray(y[0])) {
            y = [y];
        }
        if(!Array.isArray(labels)) {
            labels = [labels];
        }
        this.validateData(x, y, labels);
        y = y.map((arr, idx) => {return {"data": arr, "label":labels[idx]}});
        const ch = new Chart(
            this.container,
            {
                type: this.type,
                options: this.options,
                data: {
                    labels: x,
                    datasets: y,
                    hoverOffset: 4,
                },
                plugins: this.plugins,
            }
        );
        this.themeResponsive(ch);
        // ch();
        this.chart = ch;
        return ch;
    }

    /**
     * Renders a chart with datasets in format according to the Object[] format specified in the Chart.js documentation.
     *
     * @param {Array} datasets
     */
    renderDatasets(datasets){
        if(Object.keys(this.options).length === 0){
            this.options = this.generateDefaultOptions();
        }
        const ch = new Chart(
            this.container,
            {
                type: this.type,
                options: this.options,
                data: {
                    datasets: datasets,
                    hoverOffset: 4,
                },
                plugins: this.plugins,
            }
        );
        this.themeResponsive(ch);
        this.chart = ch;
        return ch;
    }

    validateData(x, y, labels) {
        if(labels.length !== y.length) throw new Error("The length of labels and y points must be the same")
        // TBD
        // (Array.isArray(y), "y must be an array");
        // assert(x.length === y.length, "Length of x and y must be the same");
    }

    generateDefaultOptions() {
        let options = {
            responsive: true,
            plugins: {
                legend: {
                    position: "top",
                    labels: {
                        font: {
                            size: 15,
                        },
                        color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['text'],
                    }
                },
                // tooltip: {
                //     callbacks: {
                //         label: function(tooltipItem) {
                //             let chart = tooltipItem.chart;
                //             let datasets = chart.data.datasets;
                //             let datasetIndex = tooltipItem.datasetIndex;
                //             let dataIndex = tooltipItem.dataIndex;
                //
                //             // Calculate total value for the stack at this index
                //             let total = datasets.reduce((sum, dataset) => sum + dataset.data[dataIndex], 0);
                //             let value = tooltipItem.raw;
                //             let percentage = ((value / total) * 100).toFixed(2) + "%";
                //
                //             return `${datasets[datasetIndex].label}: ${value} (${percentage})`;
                //         }
                //     }
                // }
            },
        }
        if(this.type === "bar"){
            options.scales = {
                x: {
                    stacked: this.stacked,
                    grid: {color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['scaleLines']},
                    ticks: {color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['scaleLines']},
                },
                y: {
                    stacked: this.stacked,
                    grid: {color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['scaleLines']},
                    ticks: {color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['scaleLines']},
                }
            }
        }
        if(this.stacked === true) {
            options.plugins.tooltip = options.plugins.tooltip ?? {};
            options.plugins.tooltip.callbacks = {
                label: function (tooltipItem) {
                    let chart = tooltipItem.chart;
                    let datasets = chart.data.datasets;
                    let datasetIndex = tooltipItem.datasetIndex;
                    let dataIndex = tooltipItem.dataIndex;

                    // Calculate total value for the stack at this index
                    let total = datasets.reduce((sum, dataset) => sum + dataset.data[dataIndex], 0);
                    let value = tooltipItem.raw;
                    let percentage = ((value / total) * 100).toFixed(2) + "%";

                    return `${datasets[datasetIndex].label}: ${value} (${percentage})`;
                }
            }
        }
        return options;
    }

    themeResponsive(chart) {
        const onThemeChange = (theme) => {
            chart.options.plugins.legend.labels.color = this.chartColorMap.get(theme)["text"];
            try {chart.options.plugins.datalabels.color = this.chartColorMap.get(theme)["text"];} catch (e) {}
            if(chart.config.type === "bar" || chart.config.type === "line") {
                chart.options.scales.x.grid.color = this.chartColorMap.get(theme)['scaleLines'];
                chart.options.scales.x.ticks.color = this.chartColorMap.get(theme)['scaleLines'];
                chart.options.scales.y.grid.color = this.chartColorMap.get(theme)['scaleLines'];
                chart.options.scales.y.ticks.color = this.chartColorMap.get(theme)['scaleLines'];
            }
            chart.update();
        }
        document.addEventListener('theme-change', (e) => {
            onThemeChange(e.detail.theme);
        });
        onThemeChange(localStorage.getItem('theme') ?? 'dark');
    }

    createDataLabels(dataLabelsOptions={}) {
       if(Object.keys(dataLabelsOptions).length === 0) {
           this.options.plugins.datalabels = {
               color: this.chartColorMap.get(localStorage.getItem('theme') ?? 'dark')['text'],
               anchor: 'end',
               align: 'end',
               font: {
                  weight: 'bold'
               },
               formatter: (value) => value.y > 0 ? value.y : null,
           }
       }
       else {
          this.options.plugins.datalabels = dataLabelsOptions;
       }
         this.plugins.push(ChartDataLabels);
    }

    addZooming(mode='xy', zoomOptions={}) {
        if(Object.keys(zoomOptions).length === 0) {
            this.options.plugins.zoom = {
               zoom: {
                     drag: {
                         enabled: true,
                     },
                     pinch: {
                         enabled: true,
                     },
                     mode: mode,
                },
                pan: {
                    enabled: true,
                    mode: mode,
                },
            }
        }
        else {
            this.options.plugins.zoom = zoomOptions;
        }
        this.plugins.push(zoomPlugin);
        this.container.addEventListener("dblclick", (e) => {
            const chart = this.chart;
            if (chart) {
                chart.resetZoom();
            }
        });
    }

    addSpaceBelowLegend(space=25) {
        this.plugins.unshift({
            beforeInit: function(chart, options) {
                const originalFit = chart.legend.fit;
                chart.legend.fit = function() {
                    originalFit.bind(chart.legend)();
                    this.height += space;
                }
            }
        });
    }
}

export {STChart};